import { useState } from "react";

/* ─── BRAND TOKENS ───────────────────────────────────────────────────── */
const T = {
  cyan:    "#00C8FF",
  black:   "#080808",
  white:   "#FFFFFF",
  grey:    "#777777",
  surface: "#1A1A1A",
  border:  "#2A2A2A",
  xs:  "8px",  sm: "16px", md: "24px",
  lg:  "32px", xl: "48px", xl2: "64px",
  xl3: "80px", xl4:"100px",
};

const FONT_URL = "https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;700;800;900&family=Barlow:wght@400;500&display=swap";

/* ─── TYPE HELPERS ───────────────────────────────────────────────────── */
const fDisplay = (size = "clamp(52px,10vw,88px)") => ({ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, fontSize: size, lineHeight: 0.9, textTransform: "uppercase", letterSpacing: "-1px" });
const fHeadline = (size = "clamp(28px,5vw,42px)") => ({ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, fontSize: size, lineHeight: 1.0, textTransform: "uppercase" });
const fH2 = (size = "clamp(22px,4vw,36px)") => ({ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: size, textTransform: "uppercase", lineHeight: 1.05 });
const fLabel = (size = "10px") => ({ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: size, textTransform: "uppercase", letterSpacing: "0.25em" });
const fBody = (size = "16px") => ({ fontFamily: "'Barlow', sans-serif", fontWeight: 400, fontSize: size, lineHeight: "165%" });
const fCaption = { fontFamily: "'Barlow', sans-serif", fontWeight: 400, fontSize: "13px", lineHeight: "165%" };

/* ─── LOGO COMPONENT ─────────────────────────────────────────────────── */
// Replace the src with your hosted logo path once deployed e.g. "/images/logo.png"
const LOGO_SRC = "/power-coaching-logo.png";

function Logo({ size = 120 }) {
  return (
    <img
      src={LOGO_SRC}
      alt="Power Coaching"
      style={{ width: size, height: "auto", display: "block" }}
    />
  );
}

/* ─── BRAND STAMP ─────────────────────────────────────────────────────── */
function BrandStamp() {
  return (
    <div style={{ marginTop: T.xl3, opacity: 0.18 }}>
      <Logo size={80} />
    </div>
  );
}

/* ─── SYSTEM PROMPT ──────────────────────────────────────────────────── */
const SYSTEM_PROMPT = `You are Sam, head coach at Power Coaching. You are writing a personal coaching analysis directly to an athlete based on their quiz answers. Write in first person as Sam — warm, direct, expert. Like a voice note or message from a coach who has genuinely looked at their answers. Use "I", "when I look at", "what I'm seeing here", "in my experience", "this is something I work on with a lot of athletes" etc. Never sound like AI generated this. Never be generic.

COACHING PHILOSOPHY:
- Aerobic base always before speed work
- 80/20 rule: ~80% easy/conversational, ~20% hard effort
- Strength training is NON-NEGOTIABLE: 2-3 sessions/week minimum
- Strength focus: posterior chain, single leg, plyometrics, rotational core, calf/Achilles
- Train in phases: Foundation → Build → Peak → Taper (running AND strength)
- Easy runs must be fully conversational
- Walking during easy runs = good discipline, never penalise
- Walking during tempo/hard sessions = aerobic base not ready
- Normal niggle: slight tightness after long run or hard session. Red flag: persistent/sharp/early-onset pain during easy efforts
- Score on HABITS AND APPROACH, not fitness level or experience
- A beginner doing the right things scores HIGH — never penalise for being new

IMPORTANT — RUNNING WORKOUTS:
The running workout checkboxes represent sessions the athlete does across a training block or rotates through over weeks/months — NOT all in a single week. A well-structured athlete will rotate these. Assess: do they have a balance of easy/hard? Too many hard types with no easy base is a red flag. A beginner ticking only easy runs and long runs is doing the right thing.

IMPORTANT — STRENGTH TRAINING PHASE:
Assess strength checkboxes relative to training phase. Someone in a Foundation phase correctly focuses on basic strength and movement quality — not plyometrics or complex loading. Do not penalise for missing plyometrics if they are clearly in an early phase. Penalise only if an experienced athlete with no phase awareness is missing key pillars entirely.

SCORING (0–100 per attribute):
Aerobic Base: 80-100 consistent easy running appropriate to level; 50-79 some structure but inconsistent or too much hard; 20-49 sporadic/mostly hard; beginners building slowly score 50+ if approach is correct
Speed & Pace: 80-100 hard sessions intentional ~20%, controlled pacing; 50-79 some quality but too much or unstructured; beginners not penalised for no speed work
Strength & Power: 80-100 2-3x/week, right movements for their phase, progressive; 60-79 some right work but gaps or inconsistent; 30-59 1x or wrong focus; 0-29 none at all
Recovery & Resilience:
- 80-100: Structured, proactive approach — consistent 7-9hrs sleep AND soft tissue work AND dedicated rest days AND at least one other recovery habit (HRV monitoring, deload weeks, active recovery, massage). Sleep alone does not score highly.
- 60-79: Good sleep consistently PLUS 1-2 other recovery habits practiced regularly
- 40-59: Adequate sleep but recovery is mostly passive — just rest when tired, no structured approach
- 20-39: Inconsistent sleep or minimal recovery habits — reactive only
- 0-19: No real recovery strategy, ignores the need to recover
Nutrition: 80-100 periodised, race-specific strategy, practises nutrition in training; 50-79 some good habits but gaps; 20-49 basic awareness, no training-specific plan; 0-19 no strategy
Mental: 80-100 races smart, self-aware, handles pressure well; 50-79 some challenges but aware; 20-49 multiple limiters; 0-19 mental side is a significant barrier
Race Readiness: combine race date proximity + overall scores + experience. Penalise if racing soon with major gaps. Lenient with beginners with good habits and time. Factor in goal ambition vs current fitness.

STRENGTH — SPECIAL RULES:
1. Strength carries slightly more weight — a weak strength score should pull overall score and Race Readiness down more than other attributes.
2. Always dedicate at least one full paragraph to strength regardless of score.
3. If strength is HIGH: briefly acknowledge what they are doing well and specifically name what they are getting right. Then always push for the next level — more structure/phases, progressive overload, tracking progress, more specific calf/Achilles work, better periodisation. Nobody gets a "your strength is great, nothing to add."
4. If strength is LOW: be direct and specific about what is missing and why it matters. Name the specific pillars and connect gaps to real running consequences.

TONE — WRITE AS SAM:
- First person throughout
- Honest and direct — not harsh, not fluffy
- 1-2 sentences max on one strength, then move to what needs work
- Majority on GAPS and why they matter with rough timelines
- Never give the full solution — that is what the call is for
- End with a personal line from Sam that makes the call feel like the obvious next step
- Flag warning if niggle/injury is serious
- Never praise sleep of 7-9 hours as exceptional — this is the baseline expectation for any athlete. Only acknowledge sleep positively if it is combined with other proactive recovery habits.

CROSS-REFERENCING: Always cross-check answers. Too many hard workout types + no easy runs = penalise Speed & Aerobic. Walking in tempo = flag in Speed & Race Readiness. Strength: frequency + quality both count. Race date proximity + current gaps = Race Readiness.

ACTION POINTS:
Generate 3-5 action points. Each is a single string: start with a clear, specific directive (what to do) followed by a full stop, then one sentence explaining why it matters. Use movement patterns only — no exercise names (e.g. "single leg loading" not "split squats", "posterior chain work" not "deadlifts"). Be specific enough to be useful but leave the full method for the call. Make each point feel personal to their data.

OUTPUT: Return ONLY valid JSON. No markdown, no backticks, no preamble.
{
  "headline": "6-10 word ALL CAPS headline",
  "scores": { "aerobic": 0, "speed": 0, "strength": 0, "recovery": 0, "nutrition": 0, "mental": 0, "readiness": 0 },
  "summary": "2 paragraphs max. First acknowledges one strength briefly. Second sets the picture of what needs to change and creates urgency. No full solutions.",
  "action_points": ["Action point 1", "Action point 2", "Action point 3", "Action point 4", "Action point 5"],
  "warning": null
}
Set warning to a 1-sentence string in Sam's voice if niggle is a red flag. Otherwise null.`;

/* ─── QUESTIONS ──────────────────────────────────────────────────────── */
const questions = [
  { id: "experience", type: "text", q: "HOW LONG HAVE YOU BEEN RUNNING AND WHAT'S YOUR BACKGROUND?", sub: "Tell us in your own words — there are no wrong answers", placeholder: "e.g. I've been running for about 2 years. I did a lot of sport growing up but only got into running recently. I completed my first 10K last year..." },
  { id: "raceDistance", type: "select", q: "WHAT'S YOUR TARGET RACE DISTANCE?", sub: "Select the event you're training towards", options: ["5K", "10K", "Half Marathon", "Marathon", "Ultra Marathon", "Other"] },
  { id: "racedBefore", type: "select", q: "HAVE YOU RACED THIS DISTANCE BEFORE?", sub: "Be honest — this helps us set the right benchmark", options: ["Yes — I have a PB for this distance", "I've done it but don't have an official time", "No — this will be my first time"] },
  { id: "currentPB", type: "text", q: "WHAT IS YOUR CURRENT PB FOR THIS DISTANCE?", sub: "As accurate as you can — hours, minutes, seconds", placeholder: "e.g. 1:58:32 or around 2 hours", conditional: { id: "racedBefore", value: "Yes — I have a PB for this distance" } },
  { id: "raceDate", type: "date", q: "WHAT IS YOUR RACE DATE?", sub: "If you have multiple races, enter your next / most important one" },
  { id: "raceGoal", type: "select", q: "WHAT'S YOUR #1 GOAL FOR THIS RACE?", sub: "Be honest about what success looks like for you", options: ["Just finish and enjoy it", "Beat a previous time", "Hit a specific target time", "Age group place or podium", "Build fitness — no race goal yet"] },
  { id: "goalTime", type: "text", q: "WHAT IS YOUR GOAL TIME?", sub: "Be as specific as you can", placeholder: "e.g. Sub 1:45 or exactly 1:42:00", conditional: { id: "raceGoal", values: ["Beat a previous time", "Hit a specific target time"] } },
  { id: "easyRunFeel", type: "select", q: "HOW DO YOUR EASY RUNS FEEL?", sub: "Be honest — what's your typical easy run like?", options: ["Fully conversational — I could hold a full conversation", "Mostly comfortable but breathing a bit hard", "Quite hard — I'm pushing on most runs", "I don't really do easy runs, I just run"] },
  { id: "mileage", type: "text", q: "DESCRIBE YOUR CURRENT WEEKLY TRAINING.", sub: "How many runs per week, roughly how many miles, and what they look like", placeholder: "e.g. I run 4 times a week, around 25 miles. I do one long run on Sunday, one harder session mid-week and the rest are easy..." },
  { id: "runningWorkouts", type: "checkbox", q: "WHICH RUNNING SESSIONS DO YOU DO ACROSS YOUR TRAINING?", sub: "Tick everything you do — these can be across your block, not necessarily every week", options: ["Easy / recovery runs", "Long runs", "Tempo runs", "Threshold intervals", "VO2 max intervals", "Over/unders", "Fartlek / unstructured speed play", "Strides", "Progression runs (negative split)", "Race pace runs", "Hills / incline work", "Track sessions", "None of the above / I just run"] },
  { id: "strengthFrequency", type: "select", q: "HOW OFTEN DO YOU STRENGTH TRAIN?", sub: "Be honest — consistency matters more than intention", options: ["Never", "Occasionally (less than once a week)", "1x per week", "2x per week", "3x or more per week"] },
  { id: "strengthWork", type: "checkbox", q: "WHAT DOES YOUR STRENGTH TRAINING INCLUDE?", sub: "Tick everything you currently do — based on your current training phase", options: ["Posterior chain work (deadlifts, hip thrusts, glute work)", "Single leg exercises (split squats, lunges, single leg RDLs)", "Plyometrics (jumps, bounds, hops)", "Rotational core work", "Calf & Achilles work (calf raises, soleus work)", "Ankle & foot strengthening", "Hip strengthening (abductors/adductors)", "Glute activation (banded walks, clamshells)", "Balance & stability work (single leg balance, wobble board)", "Mobility & flexibility work", "Running-specific strength movements", "Running drills (A-skips, B-skips, strides)", "Train in phases (Foundation → Build → Peak → Taper)", "Cross training (cycling, swimming, elliptical)", "Foam rolling / soft tissue work", "Stretching routine (post session)", "Upper body / general gym work", "None of the above"] },
  { id: "recovery", type: "checkbox", q: "WHAT DOES YOUR RECOVERY CURRENTLY LOOK LIKE?", sub: "Tick everything you consistently do", options: ["7–9 hours sleep consistently", "Wind-down routine before bed", "Dedicated rest days (no training)", "Active recovery sessions (light walk, swim, mobility)", "Foam rolling / soft tissue work", "Massage or sports therapy", "Cold / contrast therapy", "Compression garments", "Heart rate / HRV monitoring", "Structured deload weeks built into my plan", "None of the above"] },
  { id: "nutrition", type: "checkbox", q: "WHAT DOES YOUR NUTRITION LOOK LIKE?", sub: "Tick everything you consistently do", options: ["Consistent meal times day to day", "Increase carb / calorie intake before big training days", "Carb loading protocol before races", "Planned race nutrition strategy", "Practice race nutrition on long training runs", "Fuel during runs over 60–75 minutes", "Prioritise protein intake for recovery", "Stay on top of daily hydration (not just training days)", "No real nutritional strategy currently"] },
  { id: "mentalChallenges", type: "checkbox", q: "WHAT ARE YOUR MENTAL CHALLENGES?", sub: "Tick everything that resonates — be honest", options: ["I go out too fast and blow up early in races", "I struggle with self-doubt mid-race", "I fall apart in the final miles", "I find it hard to stay motivated in training", "I struggle to trust my training on race day", "I compare myself to other runners too much", "I get nervous and don't perform well on race day", "I push through pain when I should back off", "Mental side is generally a strength for me"] },
  { id: "niggles", type: "text", q: "ANY CURRENT NIGGLES OR INJURIES?", sub: "Be specific — when does it happen, where, and how bad is it?", placeholder: "e.g. Slight tightness in my left calf after long runs, usually goes away the next day. Or nothing at the moment..." },
];

const ATTRIBUTES = [
  { key: "aerobic",   label: "AEROBIC BASE",         icon: "🏃" },
  { key: "speed",     label: "SPEED & PACE",          icon: "⚡" },
  { key: "strength",  label: "STRENGTH & POWER",      icon: "💪" },
  { key: "recovery",  label: "RECOVERY & RESILIENCE", icon: "🔄" },
  { key: "nutrition", label: "NUTRITION & FUELLING",  icon: "🥗" },
  { key: "mental",    label: "MENTAL TOUGHNESS",      icon: "🧠" },
  { key: "readiness", label: "RACE READINESS",        icon: "🎯" },
];

function scoreColor(s) { return s >= 75 ? T.cyan : s >= 50 ? T.white : s >= 25 ? T.grey : "#444"; }
function scoreLabel(s) { return s >= 75 ? "STRONG" : s >= 50 ? "DEVELOPING" : s >= 25 ? "NEEDS WORK" : "CRITICAL GAP"; }
function isVisible(q, ans) {
  if (!q.conditional) return true;
  const v = ans[q.conditional.id];
  if (q.conditional.value) return v === q.conditional.value;
  if (q.conditional.values) return (q.conditional.values).includes(v);
  return true;
}

/* ─── SMALL COMPONENTS ───────────────────────────────────────────────── */
function Eyebrow({ children }) {
  return <p style={{ ...fLabel("10px"), color: T.cyan, marginBottom: T.md }}>{children}</p>;
}
function PrimaryBtn({ children, onClick, active = true, href }) {
  const s = { display: "inline-block", background: active ? T.cyan : T.surface, color: active ? T.black : T.border, border: "none", padding: `${T.sm} ${T.xl}`, cursor: active ? "pointer" : "not-allowed", ...fLabel("12px"), letterSpacing: "0.2em", textDecoration: "none", transition: "opacity 0.15s" };
  if (href) return <a href={href} target="_blank" rel="noopener noreferrer" style={s}>{children}</a>;
  return <button onClick={onClick} disabled={!active} style={s}>{children}</button>;
}
function GhostBtn({ children, onClick }) {
  return (
    <button onClick={onClick} style={{ background: "transparent", color: T.grey, border: `1px solid ${T.border}`, padding: `${T.sm} ${T.md}`, cursor: "pointer", ...fLabel("11px"), letterSpacing: "0.2em", transition: "all 0.15s" }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = T.grey; e.currentTarget.style.color = T.white; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.grey; }}
    >{children}</button>
  );
}
function OptionBtn({ label, selected, onClick }) {
  return (
    <button onClick={onClick} style={{ background: selected ? `${T.cyan}14` : T.surface, border: `1px solid ${selected ? T.cyan + "60" : T.border}`, color: selected ? T.white : T.grey, padding: `${T.sm} ${T.md}`, textAlign: "left", cursor: "pointer", width: "100%", ...fBody("14px"), transition: "all 0.15s" }}
      onMouseEnter={e => { if (!selected) { e.currentTarget.style.borderColor = "#444"; e.currentTarget.style.color = T.white; } }}
      onMouseLeave={e => { if (!selected) { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.grey; } }}
    >{label}</button>
  );
}
function CheckBtn({ label, checked, onClick }) {
  return (
    <button onClick={onClick} style={{ background: checked ? `${T.cyan}10` : T.surface, border: `1px solid ${checked ? T.cyan + "50" : T.border}`, color: checked ? T.white : T.grey, padding: "12px 14px", textAlign: "left", cursor: "pointer", width: "100%", ...fCaption, display: "flex", alignItems: "center", gap: T.sm, transition: "all 0.15s" }}>
      <div style={{ width: 14, height: 14, flexShrink: 0, background: checked ? T.cyan : "transparent", border: checked ? "none" : `1px solid #3a3a3a`, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {checked && <span style={{ fontSize: 9, color: T.black, fontWeight: 900 }}>✓</span>}
      </div>
      {label}
    </button>
  );
}

const inputSt = { width: "100%", background: T.surface, border: `1px solid ${T.border}`, color: T.white, padding: T.sm, ...fBody("15px"), outline: "none", transition: "border-color 0.2s", boxSizing: "border-box" };

/* ─── MAIN ───────────────────────────────────────────────────────────── */
export default function PowerCoachingQuiz() {
  const [step, setStep]           = useState("intro");
  const [qi, setQi]               = useState(0);
  const [answers, setAnswers]     = useState({});
  const [email, setEmail]         = useState("");
  const [firstName, setFirstName] = useState("");
  const [results, setResults]     = useState(null);
  const [error, setError]         = useState(null);
  const [fade, setFade]           = useState(false);

  const Q = questions[qi];

  function answer(v) { setAnswers(p => ({ ...p, [Q.id]: v })); }
  function toggle(o) {
    const cur = answers[Q.id] || [];
    setAnswers(p => ({ ...p, [Q.id]: cur.includes(o) ? cur.filter(x => x !== o) : [...cur, o] }));
  }
  function canNext() {
    const v = answers[Q.id];
    if (!v) return false;
    if (Q.type === "text") return v.trim().length > 5;
    if (Q.type === "checkbox") return v.length > 0;
    if (Q.type === "date") return v.length > 0;
    return true;
  }
  function nav(dir) {
    setFade(true);
    setTimeout(() => {
      if (dir > 0) {
        let next = qi + 1;
        while (next < questions.length && !isVisible(questions[next], answers)) next++;
        if (next < questions.length) setQi(next);
        else setStep("email");
      } else if (dir < 0) {
        let prev = qi - 1;
        while (prev >= 0 && !isVisible(questions[prev], answers)) prev--;
        if (prev >= 0) setQi(prev);
      }
      setFade(false);
    }, 150);
  }

  async function submit() {
    if (!email || !firstName) return;
    setStep("loading");

    const raceDate = answers.raceDate ? new Date(answers.raceDate) : null;
    const today = new Date();
    const daysToRace = raceDate ? Math.round((raceDate - today) / (1000 * 60 * 60 * 24)) : null;
    const raceDateStr = raceDate ? `${raceDate.toDateString()} (${daysToRace} days away)` : "Not specified";

    const body = questions.map(q => {
      if (q.id === "raceDate") return `Race date: ${raceDateStr}`;
      const a = answers[q.id];
      return `${q.q}: ${Array.isArray(a) ? a.join(", ") : a || "Not answered"}`;
    }).join("\n");

    try {
      const r = await fetch("/api/generate-report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          system: SYSTEM_PROMPT,
          messages: [{ role: "user", content: `Athlete: ${firstName}\nEmail: ${email}\n\n${body}\n\nReturn the JSON report.` }],
          firstName,
          email,
          answers,
        }),
      });
      const d = await r.json();
      const txt = d.content.map(i => i.text || "").join("");
      const parsed = JSON.parse(txt.replace(/```json|```/g, "").trim());
      setResults({ ...parsed, firstName });
      setStep("results");
    } catch {
      setError("Something went wrong. Please try again.");
      setStep("email");
    }
  }

  const overall = results
    ? Math.round(Object.values(results.scores).reduce((a, b) => a + b, 0) / 7)
    : 0;

  const todayStr = new Date().toISOString().split("T")[0];

  return (
    <div style={{ minHeight: "100vh", background: T.black, color: T.white, fontFamily: "'Barlow', sans-serif" }}>
      <link rel="stylesheet" href={FONT_URL} />
      <div style={{ position: "fixed", top: -100, right: -100, width: 500, height: 500, borderRadius: "50%", background: `radial-gradient(circle, ${T.cyan}07 0%, transparent 65%)`, pointerEvents: "none" }} />

      <div style={{ maxWidth: 640, margin: "0 auto", padding: `0 ${T.md}` }}>

        {/* ── INTRO ── */}
        {step === "intro" && (
          <div style={{ padding: `${T.xl3} 0 ${T.xl4}`, animation: "fadeUp .5s ease" }}>
            <div style={{ marginBottom: T.xl }}>
              <Logo size={160} />
            </div>
            <h1 style={{ ...fDisplay(), marginBottom: T.lg }}>
              YOUR<br />FREE<br /><span style={{ color: T.cyan }}>ATHLETE</span><br />REPORT.
            </h1>
            <div style={{ width: 40, height: 2, background: T.cyan, marginBottom: T.lg }} />
            <p style={{ ...fBody(), color: T.grey, maxWidth: 400, marginBottom: T.xl }}>
              13 questions about your training. A personalised breakdown of where you are — and what needs to change before your next race.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: T.lg, marginBottom: T.xl }}>
              {["7 scored attributes", "AI-powered analysis", "~4 minutes"].map(item => (
                <div key={item} style={{ display: "flex", alignItems: "center", gap: T.xs }}>
                  <div style={{ width: 4, height: 4, background: T.cyan }} />
                  <span style={{ ...fLabel("11px"), color: T.grey }}>{item}</span>
                </div>
              ))}
            </div>
            <PrimaryBtn onClick={() => setStep("quiz")}>START THE QUIZ →</PrimaryBtn>
            <BrandStamp />
          </div>
        )}

        {/* ── QUIZ ── */}
        {step === "quiz" && (
          <div style={{ padding: `${T.xl} 0`, opacity: fade ? 0 : 1, transition: "opacity .15s" }}>
            {/* Progress */}
            <div style={{ marginBottom: T.xl }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: T.xs }}>
                <Logo size={80} />
                {(() => {
                  const visible = questions.filter(q => isVisible(q, answers));
                  const pos = visible.findIndex(q => q.id === Q.id) + 1;
                  return <span style={{ ...fLabel("11px"), color: T.grey }}>{pos} / {visible.length}</span>;
                })()}
              </div>
              <div style={{ height: 2, background: T.surface }}>
                {(() => {
                  const visible = questions.filter(q => isVisible(q, answers));
                  const pos = visible.findIndex(q => q.id === Q.id) + 1;
                  return <div style={{ height: "100%", background: T.cyan, width: `${(pos / visible.length) * 100}%`, transition: "width .35s ease" }} />;
                })()}
              </div>
            </div>

            <h2 style={{ ...fHeadline(), marginBottom: T.xs }}>{Q.q}</h2>
            {Q.sub && <p style={{ ...fCaption, color: T.grey, marginBottom: T.lg }}>{Q.sub}</p>}

            {Q.type === "select" && (
              <div style={{ display: "flex", flexDirection: "column", gap: T.xs, marginBottom: T.lg }}>
                {Q.options.map(o => <OptionBtn key={o} label={o} selected={answers[Q.id] === o} onClick={() => answer(o)} />)}
              </div>
            )}
            {Q.type === "checkbox" && (
              <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: T.lg }}>
                {Q.options.map(o => <CheckBtn key={o} label={o} checked={(answers[Q.id] || []).includes(o)} onClick={() => toggle(o)} />)}
              </div>
            )}
            {Q.type === "text" && (
              <div style={{ marginBottom: T.lg }}>
                <textarea value={answers[Q.id] || ""} onChange={e => answer(e.target.value)} placeholder={Q.placeholder} rows={4}
                  style={{ ...inputSt, resize: "vertical", lineHeight: "165%" }}
                  onFocus={e => e.target.style.borderColor = `${T.cyan}60`}
                  onBlur={e => e.target.style.borderColor = T.border}
                />
              </div>
            )}
            {Q.type === "date" && (
              <div style={{ marginBottom: T.lg }}>
                <input type="date" min={todayStr} value={answers[Q.id] || ""} onChange={e => answer(e.target.value)}
                  style={{ ...inputSt, colorScheme: "dark" }}
                  onFocus={e => e.target.style.borderColor = `${T.cyan}60`}
                  onBlur={e => e.target.style.borderColor = T.border}
                />
                <p style={{ ...fCaption, color: T.grey, marginTop: T.xs }}>If you have multiple races, enter your next / most important one.</p>
                <button onClick={() => { answer("No race scheduled"); nav(1); }}
                  style={{ ...fLabel("11px"), color: T.grey, background: "transparent", border: "none", cursor: "pointer", marginTop: T.sm, padding: 0, letterSpacing: "0.2em" }}>
                  SKIP — NO RACE SCHEDULED →
                </button>
              </div>
            )}

            <div style={{ display: "flex", gap: T.xs }}>
              {qi > 0 && <GhostBtn onClick={() => nav(-1)}>← BACK</GhostBtn>}
              <PrimaryBtn onClick={() => nav(1)} active={canNext()}>
                {qi === questions.length - 1 ? "SEE MY RESULTS →" : "NEXT →"}
              </PrimaryBtn>
            </div>
          </div>
        )}

        {/* ── EMAIL ── */}
        {step === "email" && (
          <div style={{ padding: `${T.xl3} 0`, animation: "fadeUp .5s ease" }}>
            <div style={{ marginBottom: T.xl }}>
              <Logo size={140} />
            </div>
            <Eyebrow>ALMOST THERE</Eyebrow>
            <h2 style={{ ...fDisplay(), marginBottom: T.lg }}>
              WHERE DO<br />WE SEND<br />YOUR <span style={{ color: T.cyan }}>REPORT?</span>
            </h2>
            <div style={{ width: 40, height: 2, background: T.cyan, marginBottom: T.md }} />
            <p style={{ ...fBody(), color: T.grey, maxWidth: 380, marginBottom: T.xl }}>
              Your athlete report appears on screen immediately and gets sent to your inbox.
            </p>
            {error && <div style={{ background: "#100000", border: "1px solid #330000", padding: T.sm, marginBottom: T.md, ...fCaption, color: "#ff6666" }}>{error}</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: T.xs, marginBottom: T.lg }}>
              <input type="text" placeholder="First name" value={firstName} onChange={e => setFirstName(e.target.value)} style={inputSt}
                onFocus={e => e.target.style.borderColor = `${T.cyan}60`} onBlur={e => e.target.style.borderColor = T.border} />
              <input type="email" placeholder="Email address" value={email} onChange={e => setEmail(e.target.value)} style={inputSt}
                onFocus={e => e.target.style.borderColor = `${T.cyan}60`} onBlur={e => e.target.style.borderColor = T.border} />
            </div>
            <PrimaryBtn onClick={submit} active={!!(email && firstName)}>GENERATE MY REPORT →</PrimaryBtn>
            <p style={{ ...fCaption, color: "#2a2a2a", marginTop: T.sm }}>No spam. Used only to generate and send your report.</p>
            <BrandStamp />
          </div>
        )}

        {/* ── LOADING ── */}
        {step === "loading" && (
          <div style={{ padding: "140px 0", textAlign: "center" }}>
            <div style={{ marginBottom: T.lg, display: "flex", justifyContent: "center" }}>
              <Logo size={120} />
            </div>
            <div style={{ width: 40, height: 40, border: `2px solid ${T.surface}`, borderTop: `2px solid ${T.cyan}`, borderRadius: "50%", margin: `0 auto ${T.lg}`, animation: "spin .7s linear infinite" }} />
            <h3 style={{ ...fH2("26px"), marginBottom: T.xs }}>ANALYSING YOUR TRAINING</h3>
            <p style={{ ...fCaption, color: T.grey }}>Building your personalised athlete report</p>
          </div>
        )}

        {/* ── RESULTS ── */}
        {step === "results" && results && (
          <div style={{ padding: `${T.xl} 0 ${T.xl4}`, animation: "fadeUp .6s ease" }}>
            <div style={{ marginBottom: T.lg }}>
              <Logo size={120} />
            </div>
            <div style={{ width: 40, height: 2, background: T.cyan, marginBottom: T.lg }} />

            <p style={{ ...fH2("clamp(18px,3vw,26px)"), color: T.grey, marginBottom: T.xs }}>{results.firstName},</p>
            <h1 style={{ ...fDisplay("clamp(32px,6vw,56px)"), marginBottom: T.lg, letterSpacing: "-0.5px" }}>
              {results.headline.split(" ").map((w, i) => (
                <span key={i} style={{ color: i === 0 ? T.cyan : T.white }}>{w}{" "}</span>
              ))}
            </h1>

            {/* Overall */}
            <div style={{ display: "flex", alignItems: "center", gap: T.lg, marginBottom: T.xl, paddingBottom: T.xl, borderBottom: `1px solid ${T.border}` }}>
              <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, fontSize: 80, lineHeight: 1, color: scoreColor(overall) }}>{overall}</div>
              <div>
                <p style={{ ...fLabel("10px"), color: T.grey, marginBottom: "6px" }}>OVERALL SCORE</p>
                <p style={{ ...fLabel("13px"), color: scoreColor(overall) }}>{scoreLabel(overall)}</p>
              </div>
            </div>

            {/* Warning */}
            {results.warning && (
              <div style={{ background: "#0d0600", border: "1px solid #3a1a00", padding: `${T.sm} ${T.md}`, marginBottom: T.xl, display: "flex", gap: T.sm, alignItems: "flex-start" }}>
                <span style={{ fontSize: 15, flexShrink: 0 }}>⚠️</span>
                <p style={{ ...fCaption, color: "#ffaa55", lineHeight: "165%" }}>{results.warning}</p>
              </div>
            )}

            {/* Score bars */}
            <div style={{ marginBottom: T.xl }}>
              <p style={{ ...fLabel("10px"), color: T.grey, marginBottom: T.md }}>ATTRIBUTE BREAKDOWN</p>
              {ATTRIBUTES.map((attr, i) => {
                const s = results.scores[attr.key];
                const c = scoreColor(s);
                return (
                  <div key={attr.key} style={{ marginBottom: T.md }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: T.sm }}>
                        <span>{attr.icon}</span>
                        <span style={{ ...fLabel("12px"), color: T.white }}>{attr.label}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: T.md }}>
                        <span style={{ ...fLabel("10px"), color: c }}>{scoreLabel(s)}</span>
                        <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, fontSize: 26, color: c, lineHeight: 1 }}>{s}</span>
                      </div>
                    </div>
                    <div style={{ height: 2, background: T.surface }}>
                      <div style={{ height: "100%", background: c, width: `${s}%`, transition: `width 1s ease ${i * 0.09}s`, animation: `barFill 1s ease ${i * 0.09}s both` }} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Summary */}
            <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: T.lg, marginBottom: T.xl }}>
              <p style={{ ...fLabel("10px"), color: T.grey, marginBottom: T.md }}>COACHING ANALYSIS · SAM</p>
              {results.summary.split("\n\n").map((para, i) => (
                <p key={i} style={{ ...fBody(), color: "#999", marginBottom: T.md }}>{para}</p>
              ))}
            </div>

            {/* Action Points */}
            {results.action_points && results.action_points.length > 0 && (
              <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: T.lg, marginBottom: T.xl }}>
                <p style={{ ...fLabel("10px"), color: T.grey, marginBottom: T.md }}>YOUR ACTION POINTS</p>
                <div style={{ display: "flex", flexDirection: "column", gap: T.sm }}>
                  {results.action_points.map((point, i) => {
                    const dotIndex = point.indexOf(".");
                    const first = dotIndex !== -1 ? point.slice(0, dotIndex + 1) : point;
                    const rest = dotIndex !== -1 ? point.slice(dotIndex + 1).trim() : "";
                    return (
                      <div key={i} style={{ background: T.surface, border: `1px solid ${T.border}`, padding: T.md, display: "flex", gap: T.md, alignItems: "flex-start" }}>
                        <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, fontSize: 28, color: T.cyan, lineHeight: 1, flexShrink: 0, minWidth: 36 }}>
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <p style={{ ...fBody("15px"), margin: 0 }}>
                          <span style={{ color: T.cyan }}>{first}</span>
                          {rest && <span style={{ color: T.white }}>{" "}{rest}</span>}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* CTA */}
            <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: T.xl }}>
              <h3 style={{ ...fDisplay("clamp(36px,6.5vw,58px)"), marginBottom: T.md }}>
                BOOK YOUR FREE CALL — AND GET A FREE WEEK OF PROJECT POWER
              </h3>
              <p style={{ ...fBody(), color: T.grey, maxWidth: 360, marginBottom: T.lg }}>
                Complete your strategy call with Sam and your first week of Project Power is on me. No commitment required.
              </p>
              <PrimaryBtn href="https://calendly.com/sam-powerfxcoaching/30min">BOOK YOUR FREE CALL →</PrimaryBtn>
            </div>

            <BrandStamp />
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes barFill { from { width: 0 !important; } }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        textarea::placeholder, input::placeholder { color: #333; }
        button { border-radius: 0 !important; }
        input[type="date"]::-webkit-calendar-picker-indicator { filter: invert(0.4); }
      `}</style>
    </div>
  );
}
